import {
  ConditionDataSource,
  conditionDataSourceToFriendly,
  ConditionExpression,
  getInlineSegmentSummary,
} from "@redotech/redo-flows-common/steps";
import { SchemaBooleanExpression } from "@redotech/redo-flows-core-schema-common/expressions";
import { numberComparisonToLabel } from "@redotech/redo-flows-core-schema-common/expressions/number-expression";
import { stringMatchOperatorToLabel } from "@redotech/redo-flows-core-schema-common/expressions/text-expression";
import { Schema } from "@redotech/redo-flows-core-schema-common/type-system/schema";
import { variableDisplayName } from "@redotech/redo-flows-core-schema-common/type-system/variable-display";
import { t } from "@redotech/redo-i18n";
import { assertNever, DeepPartial } from "@redotech/util/type";

const MAX_LIST_DISPLAY = 3;

/** A rendered summary is a mix of prose, variable names, and compared values. */
export type ConditionSummarySegment =
  | { kind: "variable"; text: string }
  | { kind: "value"; text: string }
  | { kind: "text"; text: string };

const text = (value: string): ConditionSummarySegment => ({
  kind: "text",
  text: value,
});
const value = (raw: unknown): ConditionSummarySegment => ({
  kind: "value",
  text: String(raw),
});

function variableSegments(
  field: DeepPartial<SchemaBooleanExpression>["field"],
  schema: Schema | undefined,
): ConditionSummarySegment[] {
  const key = field?.toString() ?? "";
  return key
    ? [
        {
          kind: "variable",
          text: variableDisplayName({ key, label: schema?.fields[key]?.label }),
        },
      ]
    : [];
}

function getSchemaBooleanExpressionSummary(
  expression: DeepPartial<SchemaBooleanExpression> | undefined,
  valueLabels?: Map<string, string>,
  schema?: Schema,
): ConditionSummarySegment[] {
  switch (expression?.type) {
    case "number_comparison": {
      const field = variableSegments(expression.field, schema);
      const operator = expression.operator
        ? numberComparisonToLabel[expression.operator]
        : undefined;
      const comparisonValue = expression.comparisonValue;
      return operator && comparisonValue !== undefined
        ? [...field, text(operator), value(comparisonValue)]
        : field;
    }
    case "array_predicate":
      return variableSegments(expression.field, schema);
    case "text_match": {
      const field = variableSegments(expression.field, schema);
      const operator = expression.operator
        ? stringMatchOperatorToLabel[expression.operator]
        : undefined;
      const matched = (expression.matchValues ?? []).flatMap((v) =>
        v == null ? [] : [valueLabels?.get(v) ?? v],
      );
      const operatorSegments = operator ? [text(operator)] : [];
      if (!matched.length) {
        return [...field, ...operatorSegments];
      }
      const shown = matched.slice(0, MAX_LIST_DISPLAY).map(value);
      const overflow =
        matched.length > MAX_LIST_DISPLAY
          ? [
              text(
                t("+{{count}} more", {
                  count: matched.length - MAX_LIST_DISPLAY,
                }),
              ),
            ]
          : [];
      return [...field, ...operatorSegments, ...shown, ...overflow];
    }
    case "boolean_evaluation":
      return expression.field
        ? [
            ...variableSegments(expression.field, schema),
            text(t("is")),
            value(expression.value ?? true),
          ]
        : [];
    case "checkout_context_predicate": {
      const field = variableSegments(expression.field, schema);
      return field.length ? field : [text(t("Cart condition"))];
    }
    case undefined:
      return [];
    default:
      assertNever(expression);
  }
}

export function getConditionSummary(
  expression: DeepPartial<ConditionExpression> | undefined,
  valueLabels?: Map<string, string>,
  schema?: Schema,
): ConditionSummarySegment[] {
  if (!expression?.dataSource) {
    return [];
  }

  switch (expression.dataSource) {
    case ConditionDataSource.TriggerData:
      return getSchemaBooleanExpressionSummary(
        expression.schemaBooleanExpression,
        valueLabels,
        schema,
      );
    case ConditionDataSource.ExistingSegment:
      return [text(conditionDataSourceToFriendly[expression.dataSource])];
    case ConditionDataSource.InlineSegment:
      return [
        text(
          expression.inlineSegment
            ? getInlineSegmentSummary(expression.inlineSegment as any)
            : t("Custom segment conditions"),
        ),
      ];
    case ConditionDataSource.OfferResponse:
      return [text(t("Customer accepted last offer"))];
    case undefined:
      return [];
    default:
      assertNever(expression);
  }
}
